import type { BundleMessage, BundleMessageLocation } from '@ms-cloudpack/common-types';
import type { FunctionComponent } from 'preact';
import cx from 'classnames';
import { useCloudpack } from '../CloudpackProvider/CloudpackProvider.js';
import styles from './TaskResultItem.module.css';
import { Button } from '../Button/Button.js';

export interface TaskResultItemProps {
  item: BundleMessage;
  inputPath: string | undefined;
  /** 0-based index (will be adjusted for display) */
  index: number;
  type: 'error' | 'warning';
}

/**
 * Current and possible future bundler names, just to help with a friendlier error message
 * (we can't access the authoritative list from the bundler package here)
 */
const bundlerNames = ['ori', 'rollup', 'webpack', 'rspack', 'esbuild'];

/**
 * Error or warning item in the details of a task/bundle result.
 */
export const TaskResultItem: FunctionComponent<TaskResultItemProps> = (props) => {
  const { item, inputPath, index, type } = props;
  const { location: loc, notes = [] } = item;

  // Update the message source to be more user-friendly. Usually it's either a bundler name,
  // or some internal of cloudpack, and since the user might not know about the different bundlers
  // and certainly doesn't know about cloudpack internals, a bit of context is helpful.
  const friendlySource = bundlerNames.includes(item.source) ? `${item.source} bundler` : `cloudpack ${item.source}`;

  return (
    <div class={styles.root}>
      <div class={cx(styles.statusBar, type === 'warning' && styles.warning)}></div>
      <div class={styles.content}>
        <div class={styles.titleArea}>
          {index + 1}. <ItemLocation loc={loc} inputPath={inputPath} /> (via: {friendlySource})
        </div>
        <pre class={styles.text}>{stripAnsi(item.text)}</pre>
        {notes.length > 0 && (
          // Formatting here might not be ideal, but messages very rarely have notes
          <details>
            <summary>Expand for more details</summary>
            <ul>
              {notes.map((note, i) => (
                <li key={i}>
                  {note.location && (
                    <>
                      <ItemLocation loc={note.location} inputPath={inputPath} />
                      <br />
                    </>
                  )}
                  {note.text && <pre class={styles.text}>{stripAnsi(note.text)}</pre>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
};

/**
 * Remove ANSI codes from a string. (This is duplicated from `task-reporter` to avoid needing
 * browser support in that package, since the implementation is trivial.)
 */
function stripAnsi(text: string): string {
  // eslint-disable-next-line no-control-regex -- supposed to match a control character
  return text.replace(/\x1b\[[\d;]+(m|[AKB]\b)/g, '');
}

/**
 * Return a link-button to open the source file, or a placeholder if the location is undefined.
 */
const ItemLocation: FunctionComponent<{ loc: BundleMessageLocation | undefined; inputPath: string | undefined }> = (
  props,
) => {
  const { loc, inputPath } = props;
  const cloudpack = useCloudpack();

  if (!loc) {
    return '(path unavailable)';
  }

  const locationString = [loc.file, loc.line, loc.column].filter((val) => val !== undefined).join(':');
  // Don't make it clickable if we don't know the root path, or if the file path contains
  // webpack loader stuff. A real example:
  // ../../node_modules/.store/css-loader-virtual-bfb2cc7933/package/dist/cjs.js??ruleSet[1].rules[5].oneOf[0].use[1]!../../node_modules/.store/sass-loader-virtual-65ccb5bf2d/package/dist/cjs.js!./src/cssTest/example4.global.scss
  if (!inputPath || /[?!]/.test(locationString)) {
    return locationString;
  }

  const openSource = (): void => {
    if (!inputPath) return;
    void cloudpack.openCodeEditor.mutate({
      rootPath: inputPath,
      relativePath: loc.file,
      line: loc.line,
      column: loc.column,
    });
  };

  return (
    <Button variant="link" onClick={openSource}>
      {locationString}
    </Button>
  );
};
